import { FacetCompletionInfo, FacetsCompletionInfo } from 'common/types';
import { FacetData, LogEntry } from 'common/graphqlTypes';
import cloneDeep from 'lodash/cloneDeep';
import uniq from 'lodash/uniq';

import { DataNode, DataNodeKey, DataNodeValue } from 'common/types';
import { numericStringCompare, unquote } from 'common/util/string';
import { FIELD_TYPE, Schema } from 'schemas/types';

/**
 * Update facet completion info from one or more TOP_FACETS responses.
 */
export const updateFacetCompletionInfo = (
  facetCompletionInfo: FacetsCompletionInfo,
  facetDatas: FacetData[],
): FacetsCompletionInfo => {
  const newFacetCompletionInfo = cloneDeep(facetCompletionInfo);

  // make sure that 'message' and 'timestamp' are always available
  if (Object.keys(newFacetCompletionInfo).length === 0) {
    newFacetCompletionInfo.message = {
      type: FIELD_TYPE.TEXT,
      values: [],
    };
    newFacetCompletionInfo.timestamp = {
      type: FIELD_TYPE.TIMESTAMP,
      values: [],
    };
  }

  facetDatas.forEach((facetData) => {
    facetData.facets.forEach((facet) => {
      if (!newFacetCompletionInfo[facet.name]) {
        newFacetCompletionInfo[facet.name] = {
          type: FIELD_TYPE.TEXT,
          values: [],
        } as FacetCompletionInfo;
      }
      const facetInfo = newFacetCompletionInfo[facet.name];
      if (facet.isNumeric) {
        facetInfo.type = FIELD_TYPE.NUMBER;
      }
      facetInfo.values = (facetInfo.values || []).concat(facet.values.map((value) => unquote(value.value)));
      facetInfo.values = uniq(facetInfo.values).sort();
      // facet is boolean if only values are 'true' and/or 'false'
      if (
        facetInfo.values.length <= 2 &&
        facetInfo.values.filter((value) => value !== 'true' && value !== 'false').length === 0
      ) {
        facetInfo.type = FIELD_TYPE.BOOLEAN;
      }
    });
  });

  return newFacetCompletionInfo;
};

/**
 * Update facet completion info based on a given schema.
 */
export const updateFacetCompletionInfoFromSchema = (
  facetCompletionInfo: FacetsCompletionInfo,
  schema: Schema,
): FacetsCompletionInfo => {
  const newFacetCompletionInfo = cloneDeep(facetCompletionInfo);

  Object.keys(schema.fields)
    .filter((fieldId) => !schema.fields[fieldId].notSearchable)
    .forEach((facet) => {
      if (!newFacetCompletionInfo[facet]) {
        newFacetCompletionInfo[facet] = {
          values: [],
          type: FIELD_TYPE.TEXT,
        } as FacetCompletionInfo;
      }
      const facetInfo = newFacetCompletionInfo[facet];
      facetInfo.source = schema.dataSource;
      const schemaField = schema.fields[facet];
      if (schemaField.values) {
        facetInfo.values = uniq((facetInfo.values || []).concat(schemaField.values)).sort();
      }
      facetInfo.values = uniq(facetInfo.values).sort();
      facetInfo.type = schemaField.type;
    });

  return newFacetCompletionInfo;
};

/**
 * Convert a flat map of keys and values where the keys use dot notation (eg 'src.process.image.path') into a tree where
 * the dots indicate a path (ie dot is like a slash in a file system path).
 *
 * @param source          map of key/value pairs
 *
 * @returns the root of the resulting tree
 */
export const dotNotationToTree = (source: Record<string, any>): DataNode => {
  const keys = Object.keys(source).sort(numericStringCompare);

  const root = { key: '' } as DataNode;
  let current: DataNode;
  keys.forEach((key) => {
    current = root;
    const parts = key.split('.');
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i];
      let children = current.children;
      if (!children) {
        children = current.children = [] as DataNode[];
      }
      let node = children.find((child) => child.key === part);
      if (!node) {
        node = { key: part, fullKey: key };
        if (i === parts.length - 1) {
          node.value = source[key];
        }
        children.push(node);
      }
      current = node;
    }
  });

  return root;
};

/**
 * Takes a log event and converts its facets (including system ones) into a tree. Transform can optionally
 * be applied to the keys and/or values.
 *
 * @param logEntry        a log event
 * @param allowedFacets   list of facets that can be included
 * @param filter          filter string to filter against keys and values
 * @param transformKey    function to transform a key
 * @param transformValue  function to transform a value
 *
 * @returns root of its facets tree
 */
export const getFacetsAsTree = (
  logEntry: LogEntry,
  allowedFacets?: string[],
  filter?: string | null,
  transformKey?: (node: DataNode) => DataNodeKey,
  transformValue?: (node: DataNode) => DataNodeValue,
): DataNode => {
  let facets: Record<string, any> = { ...logEntry.serverInfo, ...logEntry.values };
  if (allowedFacets) {
    facets = Object.keys(facets)
      .filter((key) => !allowedFacets || allowedFacets.includes(key))
      .reduce((acc, key) => ({ ...acc, [key]: facets[key] }), {});
  }
  if (filter) {
    facets = Object.keys(facets)
      .filter((key) => {
        const value = String(facets[key]);
        return (
          key.toLowerCase().indexOf(filter.toLowerCase()) !== -1 ||
          value.toLowerCase().indexOf(filter.toLowerCase()) !== -1
        );
      })
      .reduce((acc, key) => ({ ...acc, [key]: facets[key] }), {});
  }

  const root = dotNotationToTree(facets);
  if (!transformKey && !transformValue) {
    return root;
  }

  // Perform any requested transforms. We need to do this after the tree has been built because
  // transforms can break key comparisons that are used to add nodes to the tree.
  const traverse = (node: DataNode) => {
    if (node.value != null && transformValue) {
      node.value = transformValue(node);
    }
    node.key = transformKey ? transformKey(node) : node.key;
    if (node.children) {
      node.children.forEach((child) => traverse(child));
    }
  };

  traverse(root);

  return root;
};
